import { useState, useRef, useCallback } from "react";
import { EventSourcePolyfill } from "event-source-polyfill";
import { useQueryClient } from "@tanstack/react-query";

export function useSSE({ onStreamDone }: { onStreamDone?: () => void } = {}) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [streamEvents, setStreamEvents] = useState<unknown[]>([]);

  const streamMetaRef = useRef<Record<string, string>>({
    thought: "",
    action: "",
    observation: "",
    tool: "",
  });

  const eventSourceRef = useRef<EventSourcePolyfill | null>(null);
  const pendingConnectionRef = useRef<boolean>(false);
  const queryClient = useQueryClient();

  const disconnectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      pendingConnectionRef.current = false;
      setIsStreaming(false);
      setIsThinking(false);
      // streamEvents는 초기화하지 않음 (검색 결과 유지)
    }
  }, []);

  const connectSSE = useCallback(
    (convId: string) => {
      if (!convId || pendingConnectionRef.current || eventSourceRef.current)
        return;

      const token = localStorage.getItem("access_token");
      if (!token) return;

      const url = `${import.meta.env.VITE_API_URL}/api/v1/chat/conversations/${convId}/events`;
      const es = new EventSourcePolyfill(url, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        heartbeatTimeout: 300000,
      });

      pendingConnectionRef.current = true;

      es.onopen = () => {
        setIsStreaming(true);
        setIsThinking(true);
        setMessageContent("");
        streamMetaRef.current = {
          thought: "",
          action: "",
          action_input: "",
          observation: "",
          tool: "",
        };
        pendingConnectionRef.current = false;
      };

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("📨 SSE message received:", data);

          if (data.status === "started") {
            setIsStreaming(true);
            setIsThinking(true);
            setMessageContent("");
            return;
          }

          if (data.status === "processing") {
            // 처리 중 메시지는 무시하거나 로그로 표시
            console.log("Processing:", data.message);
            return;
          }

          if (data.status === "streaming" && data.chunk) {
            // 새로운 chat 엔드포인트의 스트리밍 형식
            console.log("📤 Received streaming chunk:", data.chunk);
            // 첫 번째 청크가 도착하면 "생각 중" 상태를 false로 변경
            setIsThinking(false);
            setMessageContent((prev) => {
              const newContent = prev + data.chunk;
              console.log("📝 Updated message content:", newContent);
              return newContent;
            });
            setStreamEvents((prev) => {
              const newEvents = [
                ...prev,
                {
                  type: "content",
                  content: data.chunk,
                },
              ];
              console.log("📊 Updated stream events:", newEvents);
              return newEvents;
            });
            return;
          }

          if (data.status === "completed" && data.result) {
            // 최종 결과 처리
            console.log("✅ SSE completed, disconnecting...");
            setIsStreaming(false);
            setIsThinking(false);
            disconnectSSE();

            // 검색 결과가 있으면 스트림 이벤트에 추가
            if (data.result.search_results) {
              setStreamEvents((prev) => [
                ...prev,
                {
                  type: "search_results",
                  content: data.result.content,
                  search_results: data.result.search_results,
                },
              ]);
            }

            // reference 정보가 있으면 스트림 이벤트에 추가
            if (data.result.reference) {
              setStreamEvents((prev) => [
                ...prev,
                {
                  type: "reference",
                  content: data.result.message,
                  reference: data.result.reference,
                },
              ]);
            }

            // 메시지 재조회로 최신 상태 동기화 (사용자 메시지는 유지)
            console.log("🔄 메시지 재조회 시작...");
            queryClient.invalidateQueries({
              queryKey: ["chatMessages", convId],
            });
            console.log("✅ 메시지 재조회 완료");
            onStreamDone?.();
            return;
          }

          if (data.status === "error") {
            console.error("SSE Error:", data.error);
            setIsStreaming(false);
            setIsThinking(false);
            disconnectSSE();
            return;
          }

          // 기존 형식 호환성 유지
          if (data.status === "done") {
            console.log("✅ SSE done, disconnecting...");
            setIsStreaming(false);
            setIsThinking(false);
            disconnectSSE();

            // 메시지 재조회로 최신 상태 동기화 (스트리밍 상태는 자연스럽게 전환)
            queryClient.invalidateQueries({
              queryKey: ["chatMessages", convId],
            });
            onStreamDone?.();
            return;
          }

          if (
            ["thought", "action", "action_input", "observation"].includes(
              data.type
            )
          ) {
            // 메타 이벤트가 도착하면 "생각 중" 상태를 false로 변경
            setIsThinking(false);
            setStreamEvents((prev) => [
              ...prev,
              {
                type: data.type,
                content: data.content?.replace(/\s+/g, " ") || "",
              },
            ]);
            return;
          }

          if (data.type === "answer") {
            // 답변 청크가 도착하면 "생각 중" 상태를 false로 변경
            setIsThinking(false);
            setStreamEvents((prev) => [
              ...prev,
              {
                type: "content",
                content: data.content,
              },
            ]);
            return;
          }
        } catch (error) {
          console.error("SSE parsing error:", error);
          return;
        }
      };

      es.onerror = () => {
        disconnectSSE();
      };

      eventSourceRef.current = es;
    },
    [onStreamDone, queryClient, disconnectSSE]
  );

  const sendMessage = useCallback(
    async (
      convId: string,
      content: string,
      options?: unknown,
      onDone?: () => void
    ) => {
      if (!convId || !content.trim()) return;

      const token = localStorage.getItem("access_token");
      if (!token) return;

      try {
        // 새로운 메시지 전송 시 스트리밍 상태만 초기화 (사용자 메시지는 유지)
        setIsStreaming(false);
        setIsThinking(false);
        setMessageContent("");
        setStreamEvents([]);

        // 기존 SSE 연결 해제
        disconnectSSE();

        // 일반 채팅 모드
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/chat/conversations/${convId}/messages`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ role: "user", content, options }),
          }
        );

        if (!res.ok) {
          return;
        }

        if (onDone) onDone();
      } catch (err) {
        console.error("메시지 전송 오류:", err);
        return;
      }

      // 일반 채팅 모드일 때만 SSE 연결
      connectSSE(convId);
    },
    [connectSSE, disconnectSSE]
  );

  return {
    isStreaming,
    isThinking,
    streamEvents,
    messageContent,
    sendMessage,
    connectSSE,
    disconnectSSE,
  };
}
